/**
 * @file reel.js
 * @desc This module contains Reel class definition.
 * @author Igor Kling <kling.igor.ksi@gmail.com>
 * @version 1.0.dev
 * @copyright 2016 (c) KSI Corporation
 * */

// depends on global PIXI

(/** @lends <global> */function (global) {
    'use strict';

    function mod(x, y) {
        return x - y * Math.floor(x / y);
    }

    /**
     * Constructs Reel.
     * @class
     * @constructor
     * @param {Object} barrelContainer
     * @param {Object} index
     * @param {Object} options Description object
     * @param {Number} symbolsCount Unique symbols number
     * @name Reel
     * @classdesc This is a GUI component. It is a part of complex component {@link Barrel} and never used alone.
     * */
    var Reel = function(barrelContainer, index, options, symbolsCount) {

        this.container            = new PIXI.Container();

        this.rotatingSpeed        = 1900;
        this.rotatingTime         = 2;
        this.accelerationTime     = 0.05;
        this.offset               = 0;
        this.delegate             = null;
        this.state                = null;

        this.onBumpCallback       = null;

        this._directionalOffset   = 0.0;
        this._bouncingValueIndex  = 0; // интдекс эелмента в массиве значений отскоков барабана
        this._acceleration        = 0; // текущее ускорение
        this._delayTime           = 0; // время ожидания в режиме бесконечного вращения
        this._shownSymbols        = 0; // количество остановочных символов, которые уже успели выкатится на барабан
        this._prevReel            = null;
        this._infiniteRotate      = false;
        this._elementsBatchNode   = null;

        this._kROWS               = 0;
        this._kELEMENTS           = 0;
        this._kReelIndex          = 0;
        this._kCELLSIZE           = null;
        this._rotationDirection   = null;
        this._path                = 0;
        this._speed               = 0;
        this._currentTime         = 0;
        this._isBlurEnabled       = false;

        // текущее вертикальное смещение от положения покоя символов в момент остановки барабана
        // изначально оно равно -3*_kCELLSIZE.h. При завершении состояния равно 0. В состоянии bumping тоже отсчитывается
        this._stoppingOffset        = 0;

        this._stopElements        = null;
        this._reelBouncingValues  = null;

        this._elements            = [];
        this._blurElements        = [];

        // фреймы на атласе символов
        this._spritesOnAtlas      = [];

        // фреймы на атласе блюра
        this._blurOnAtlas         = [];

        // отдельное хранение ячеек для быстрого доступа к ним
        this._cells = [];

        this._elementsBatchNode   = barrelContainer;
        this._kROWS               = options.rows;
        this._kELEMENTS           = symbolsCount;
        this._kReelIndex          = index;
        this._kCELLSIZE           = options.cellSize;
        this._isBlurEnabled       = options.blurNameFormat !== undefined && typeof options.blurNameFormat === 'string';
        this._isFreezed           = false;


        for (var i = 0; i < symbolsCount; ++i) {
            // эталонные регионы символов на общем атласе
            var symbolTexture = PIXI.utils.TextureCache[options.symbolNameFormat.$(i)];
            this._spritesOnAtlas.push(symbolTexture.frame);

            //  коды символов которые могут появляться во вращающейся ленте
            this._blurElements.push(i);

            if (this._isBlurEnabled) {
                var texture = PIXI.utils.TextureCache[options.blurNameFormat.$(i)];

                if (options.separatedBlurAtlas) {
                    texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
                }

                this._blurOnAtlas.push(texture.frame);
            }
        }

        //  учитываются заэкранные элементы верхний и нижний
        for (var row = 0; row < (this._kROWS + 2); ++row) {
            var symbolCode = this._makeSymbol();

            this._elements.push(symbolCode);


            var cell = new PIXI.Container();

            var cell_sprite = new PIXI.Sprite(PIXI.utils.TextureCache[options.symbolNameFormat.$(0)].clone());
            if ( options.cell_rescale ) {
                cell_sprite.scale.set( options.cell_rescale );
            }
            cell.addChild( cell_sprite );

            if (this._isBlurEnabled) {
                var blur_image = new PIXI.Sprite(PIXI.utils.TextureCache[options.blurNameFormat.$(0)].clone());
                blur_image.visible = false;
                cell.addChild( blur_image );
            }

            this.container.addChild(cell);

            this._cells.push(cell);
        }

        this._randomBlurSequence = true;
        this._nextBlurSymbolId = 0;

        barrelContainer.addChild(this.container);
    };

    /**
     * Enum for reel state.
     * @readonly
     * @enum {Number}
     * @memberOf Reel
     */
    Reel.State = {
        /**
         * Should not be happens
         * */
        Undefined:      0,
        /**
         * Reel is in idle state
         * */
        Idle:           1,
        /**
         * Reel is accelerating (begins rotation)
         * */
        Accelerating:   2,
        /**
         * Reel is rotating on maximum constant speed
         * */
        Rotating:       3,
        /**
         * Reel is stopping and showing valuable symbols
         * */
        Stopping:       4,
        /**
         * Reel is bouncing before stops finally
         * */
        Bouncing:       5
    };

    /**
     * Enum for reel rotation directions.
     * @readonly
     * @enum {Number}
     * @memberOf Reel
     * @todo Perhaps rotation up is not yet fully implemented
     */
    Reel.RotationDirection = {
        /**
         * Reel rotated down (default behaviour)
         * */
        Down: 0,

        /**
         * Reel rotates up
         * */
        Up: 1
    };

    Reel.prototype.constructor = Reel;

    Object.defineProperties(Reel.prototype, {
        /**
         * random sequence in blur
         * */
        randomBlurSequence: {
            set: function (isRand) {
                if (isRand) {
                    this._randomBlurSequence = true;
                }
                else {
                    this._randomBlurSequence = false;
                }
            }
        }
    });

    /**
     * Set reel in default state.
     * @memberOf Reel
     * */
    Reel.prototype.init = function () {
        this.state                = Reel.State.Idle;
        this._rotationDirection   = Reel.RotationDirection.Down;
    };

    /**
     * Returns current symbol code in blur.
     * @returns {Number} Symbol code
     * @memberOf Reel
     * @private
     * */
    Reel.prototype._makeSymbol = function() {
        if (this._randomBlurSequence) {
            this._nextBlurSymbolId = Math.floor(Math.random() * this._blurElements.length);
            return this._blurElements[this._nextBlurSymbolId];
        }
        else {
            this._nextBlurSymbolId = (this._nextBlurSymbolId + 1) % this._blurElements.length | 0;
            return this._blurElements[this._nextBlurSymbolId];
        }
    };

    /**
     * Set symbols combination to show on reel stop.
     * @param {Number[]} symbols Array of symbol codes (from top to bottom)
     * @memberOf Reel
     * */
    Reel.prototype.setStopElements = function(symbols) {
        if (this.state === Reel.State.Stopping) {
            //        cc.warn("YOU SHOULD NOT SET STOPPING ELEMENTS IN STOPPING MODE");
            return;
        }

        this._stopElements = [];
        for (var i = 0; i < this._kROWS; ++i) {
            this._stopElements.push(symbols[i]);
        }

        this._shownSymbols = 0;
    };

    /**
     * Sets bumping values.
     * @param {Number[]} values Array of shift from zero point (idle position). Last value should be equals to 0
     * @memberOf Reel
     * */
    Reel.prototype.setBouncingValues = function(values) {
        if (values === null || values === undefined) return;

        this._reelBouncingValues = [];

        var i = 0;
        while (values[i] !== 0) {
            this._reelBouncingValues.push(values[i]);
            ++i;
        }

        this._reelBouncingValues.push(0);
    };

    /**
     * Sets current valuable symbols on reel.
     * @param {Number[]} symbols Array of symbol codes (from top to bottom)
     * @memberOf Reel
     * */
    Reel.prototype.setSymbols = function(symbols) {
        if (this.state !== Reel.State.Idle) {
            //       cc.warn("YOU SHOULD NOT SET SYMBOLS IN NON IDLE STATE !!!!");
            return;
        }

        for (var i = 0; i < this._kROWS; ++i) {
            var symbolId = symbols[i];
            //        cc.assert(symbolId < this._kELEMENTS, "invalid symbol code");

            this._elements[i+1] = symbolId;

            // пропускаем верхний заэкранный символ
            var node = this._cells[i + 1];//this._elementsBatchNode.getChildByTag(tag);
            //        cc.assert(node instanceof cc.Sprite, "node is not a CCSprite");

            //       node.setSpriteFrame(cc.spriteFrameCache.getSpriteFrame("symbol_" + symbolId + ".png"));

            node.getChildAt(0).texture.frame = this._rectForSymbolAtIndex(i+1);
            // node.texture._updateUvs();
        }

        //this._updateGeometry();
    };

    /**
     * Returns cell at specified index.
     * @param {Number} row Row on the reel
     * @returns Cell (PIXI.Sprite)
     * @memberOf Reel
     * */
    Reel.prototype.getReelSymbolAtRow = function(row) {
        //    cc.assert(row < this._kROWS, "INVALID ROW");

        // var tag = this._kReelIndex * (this._kROWS + 2) + row + 1;
        // var node = this._elementsBatchNode.getChildByTag(tag);

        return this._cells[row + 1];

        //    cc.assert(node instanceof cc.Sprite, "node is not a CCSprite");

        // return node;
    };

    /**
     * Sets reel's top left corner position.
     * @param {Object} x x position
     * @param {Object} y y position
     * @memberOf Reel
     * */
    Reel.prototype.setPosition = function(x, y) {
        this.container.position.x = x;
        this.container.position.y = y;

        this._updateGeometry();
    };

    /**
     * Updates reel state.
     * @param {Number} delta Time shift after last call. All reel rotation magic is happens here.
     * @memberOf Reel
     * */
    Reel.prototype.update = function(delta) {
        if (this.state === Reel.State.Idle) return;

        //
        // console.log(delta);

        //delta = delta || 0.0167;

        // offset здесь фигурирует как абсолютная величина
        // в updateGeometry уже к нему применяется направления вращения

        var shiftingSymbols = 0;

        switch (this.state) {
            case Reel.State.Accelerating:
            {
                this._currentTime += delta;

                this._speed += this._acceleration * delta;

                if (this._currentTime >= this.accelerationTime)
                {
                    delta -= (this._currentTime - this.accelerationTime);
                    this._currentTime = 0;
                    this._speed = this.rotatingSpeed;
                    this.state = Reel.State.Rotating;


                    // переключаем в блюры!!!!

                    if (this._isBlurEnabled) {
                        for (var i = 0; i < (this._kROWS + 2); ++i) {
                            this._cells[i].getChildAt(0).visible = false;
                            this._cells[i].getChildAt(1).visible = true;
                        }
                    }



                    //                CCLOG(@"#### [REEL %d] ROTATING", kReelIndex);
                }

                this._path = delta * this._speed;

                this.offset += mod(this._path, this._kCELLSIZE.height);

                shiftingSymbols = Math.floor(this._path / this._kCELLSIZE.height) + Math.floor(this.offset / this._kCELLSIZE.height);

                this.offset = mod(this.offset, this._kCELLSIZE.height);

                if (shiftingSymbols > 0)
                {
                    this._shiftSymbols(shiftingSymbols);
                }

                break;
            }

            case Reel.State.Rotating:
            {
                this._currentTime += delta;

                if (this._infiniteRotate) {
                    var rotatingTimePreviousReels = 0.0;
                    if (this._prevReel != null && this._prevReel._isFreezed != true) {
                        rotatingTimePreviousReels = this._prevReel.rotatingTime;
                    }

                    if (this._currentTime > this.rotatingTime - rotatingTimePreviousReels) {
                        this._delayTime += delta;
                    }
                }
                else if (this._currentTime - this._delayTime > this.rotatingTime) {
                    delta -= (this._currentTime - this.rotatingTime - this._delayTime);

                    this._currentTime = 0;
                    this._delayTime = 0;
                    this.state =  Reel.State.Stopping;


                    // this._stoppingOffset = mod(this._path, this._kCELLSIZE.height) - 3 * this._kCELLSIZE.height;
                    //  выключаем блюры!!!
                    if (this._isBlurEnabled) {
                        for (var i = 0; i < (this._kROWS + 2); ++i) {
                            this._cells[i].getChildAt(0).visible = true;
                            this._cells[i].getChildAt(1).visible = false;
                        }
                    }

                    //                CCLOG(@"#### [REEL %d] STOPPING", kReelIndex);

                    //  с этого момента будут поступать заготовленные символы
                }

                this._path = delta * this._speed;

                this.offset += mod(this._path, this._kCELLSIZE.height);

                shiftingSymbols = Math.floor(this._path / this._kCELLSIZE.height) + Math.floor(this.offset / this._kCELLSIZE.height);

                this.offset = mod(this.offset, this._kCELLSIZE.height);

                if (this.state ===  Reel.State.Stopping) {
                    this._stoppingOffset = this.offset - 3 * this._kCELLSIZE.height;
                }

                if (shiftingSymbols > 0)
                {
                    this._shiftSymbols(shiftingSymbols);
                }

                break;
            }

            case Reel.State.Stopping:
            {
                // путь за время
                this._path = delta * this._speed;
                this._stoppingOffset += this._path;

                // наращиваем без учета кол-ва ячеек
                this.offset += mod(this._path, this._kCELLSIZE.height);

                // количесто целых ячеек в пройденном пути
                shiftingSymbols = Math.floor(this._path / this._kCELLSIZE.height) + Math.floor(this.offset / this._kCELLSIZE.height);

                // нормализуем
                this.offset = mod(this.offset, this._kCELLSIZE.height);

                this._stoppingOffset += this.offset + shiftingSymbols * this._kCELLSIZE.height;

                if (this.delegate.onReelStopping) {
                    this.delegate.onReelStopping(this._kReelIndex, this._stoppingOffset);
                }




                // if (this.delegate.onReelStopping) {
                //     this.delegate.onReelStopping(this._kReelIndex, this._stoppingOffset);
                // }
                // иначе еще можно выкатить новые символы
                if (this._shownSymbols < this._kROWS)
                {
                    if (shiftingSymbols > (this._kROWS - this._shownSymbols))
                    {
                        // у нас выкатилось более остановочных символов, чем нужно

                        //                   cc.log("(delta = " + delta.toFixed(2));

                        shiftingSymbols  = this._kROWS - this._shownSymbols;
                        this._shiftValuableSymbols(shiftingSymbols);

                        // выкатываем сразу же и фейковый заэкранный символы
                        this._shiftSymbols(1);

                        this.offset = 0.0;

                        this._currentTime = 0;

                        this.state = Reel.State.Bouncing;

                        this._directionalOffset = 0.0;
                        this._bouncingValueIndex = 0;

                        //                    CCLOG(@"#### [REEL %d] BOUNCING", kReelIndex);

                        this.delegate.reelDidStop(this._kReelIndex);
                    }
                    else if (shiftingSymbols > 0)
                    {
                        //CCLOG(@"--- SHIFT %d elements ---", cellsShifted);
                        this._shiftValuableSymbols(shiftingSymbols);
                    }
                }
                // если все значимые символы выкачены
                else if(this._shownSymbols === this._kROWS) {
                    // если на барабане к этому моменту произошел сдвиг на еще 1 символ
                    if (shiftingSymbols === 1) {
                        // выкатываем фейковый заэкранный символы
                        this._shiftSymbols(1);

                        this.offset = 0.0;

                        this._currentTime = 0;

                        this.state = Reel.State.Bouncing;

                        this._directionalOffset = 0.0;

                        this._bouncingValueIndex = 0;

                        //                    CCLOG(@"#### [REEL %d] BOUNCING", kReelIndex);

                        this.delegate.reelDidStop(this._kReelIndex);
                    }
                }
                break;
            }

            case Reel.State.Bouncing:
            {
                //            CCLOG(@"*** BOUNCING ***");
                //            CCLOG(@"offset: %f", offset);
                //            CCLOG(@"elements: %@", elements);

                if (this._bouncingValueIndex >= this._reelBouncingValues.length)
                {
                    this.state = Reel.State.Idle;
                    //                CCLOG(@"#### [REEL %d] IDLE", kReelIndex);
                }
                else
                {
                    this._directionalOffset = -this._reelBouncingValues[this._bouncingValueIndex];
                    ++this._bouncingValueIndex;

                    if (this.onBumpCallback) {
                        this.onBumpCallback(this._directionalOffset);
                    }

                    if (this.delegate.onReelStopping) {
                        this.delegate.onReelStopping(this._kReelIndex, this._directionalOffset);
                    }
                }
                break;
            }

            default:
                break;
        }

        this._updateGeometry();
    };

    /**
     * Starts reel rotation.
     * @memberOf Reel
     * */
    Reel.prototype.startRotate = function() {
        //cc.log("Reel " + this._kReelIndex + " startRotate");

        if (this.state === Reel.State.Idle)
        {
            this.state = Reel.State.Accelerating;

            this._speed = 0.0;
            this._acceleration = this.rotatingSpeed / this.accelerationTime;
            this._currentTime = 0.0;
            this._delayTime = 0.0;
        }
        else
        {
            //        cc.warn("YOU SHOULD NOT START ROTATION ON NON IDLE STATE");
        }
    };

    /**
     * Interrupts reel rotation (if allowed).
     * @memberOf Reel
     * */
    Reel.prototype.stopRotate = function() {
        //    cc.assert(this._infiniteRotate == false, "infiniteRotate must be NO before stopRotate!");
        if (this._infiniteRotate === true)
        {
            //        cc.warn("YOU SHOULD NOT STOP ROTATION WITH INFINITE ROTATE ON");
            return;
        }

        if (this.state === Reel.State.Rotating)
        {
            this.state = Reel.State.Stopping;

            //  выключаем блюры!!!
            if (this._isBlurEnabled) {
                for (var i = 0; i < (this._kROWS + 2); ++i) {
                    this._cells[i].getChildAt(0).visible = true;
                    this._cells[i].getChildAt(1).visible = false;
                }
            }
            this._updateGeometry();
        }
    };

    /**
     * Force reel to be in Idle state
     * @memberOf Reel
     * */
    Reel.prototype.interruptRotate = function() {
        this.state = Reel.State.Idle;

        if (this._isBlurEnabled) {
            for (var i = 0; i < (this._kROWS + 2); ++i) {
                this._cells[i].getChildAt(0).visible = true;
                this._cells[i].getChildAt(1).visible = false;
            }
        }

        this.offset               = 0;
        this._infiniteRotate      = false;
        this._path                = 0;
        this._speed               = 0;
        this._currentTime         = 0;

        this._updateGeometry();
    };

    /**
     * Shifts symbol codes buffer by specified positions filling by blur symbols.
     * This is also a part of rotation magic.
     * @param {Number} cellsShifted Number of symbols to shift to
     * @memberOf Reel
     * @private
     * */
    Reel.prototype._shiftSymbols = function(cellsShifted) {
        if (cellsShifted == 0) return;

        var shiftingElements = 0;

        if (cellsShifted < (this._kROWS + 2))
        {
            shiftingElements = cellsShifted;
        }
        // иначе заменяем весь массив целиком
        else
        {
            shiftingElements = this._kROWS + 2;
        }

        //        CCLOG(@"--- BEFORE ---");

        for (var i = 0; i < shiftingElements; ++i)
        {
            if (this._rotationDirection === Reel.RotationDirection.Down)
            {
                this._elements.pop();
                this._elements.unshift(this._makeSymbol());
            }
            else if (this._rotationDirection == Reel.RotationDirection.Up)
            {
                this._elements.shift();
                this._elements.push(this._makeSymbol());
            }
        }

        //    CCLOG(@"elements %@", elements);
        //    CCLOG(@"stopElements %@", stopElements);
        //
        //    CCLOG(@"--- AFTER ---");
    };

    /**
     * Shifts symbol codes buffer by specified positions filling by valuable symbols.
     * This is also a part of rotation magic.
     * @param {Number} cellsShifted Number of symbols to shift to
     * @memberOf Reel
     * @private
     * */
    Reel.prototype._shiftValuableSymbols = function(shiftSymbols) {
        if (this._shownSymbols >= this._kROWS) return;

        //    cc.assert(shiftSymbols <= (this._kROWS - this._shownSymbols), "trying to shift to much symbols");
        for (var i = 0; i < shiftSymbols; ++i)
        {
            if (this._rotationDirection === Reel.RotationDirection.Down)
            {
                this._elements.pop();
                this._elements.unshift(this._stopElements[this._kROWS - this._shownSymbols - 1]);
            }
            else if (this._rotationDirection === Reel.RotationDirection.Up)
            {
                this._elements.shift();
                this._elements.push(this._stopElements[this._shownSymbols]);
            }
            ++this._shownSymbols;
        }
    };

    // получения положения на атласе для символа на барабане
    // учитывается состояние барабана, статус барабана и сколько символов показалось уже если барабан останавливается
    /**
     * Returns rectangle copy on symbols atlas for specified symbol on reel.
     * @param {Number} index Symbol index on reel
     * @returns {Object} Rectangle on symbols atlas
     * @memberOf Reel
     * @private
     * */
    Reel.prototype._rectForSymbolAtIndex = function(index) {
        var elementId = this._elements[index];

        if (this._isBlurEnabled && this.state === Reel.State.Rotating)
        {
            return this._blurOnAtlas[elementId].clone();
        }

        return this._spritesOnAtlas[elementId].clone();
    };

    /*_textureOffsetForSymbolAtIndex(index) {
     var elementId = this._elements[index];

     if (this._isBlurEnabled)
     {
     if (this.state === Reel.State.Idle || this.state === Reel.State.Bouncing || this.state === Reel.State.Accelerating )
     {
     return {x:this._spritesOffsets[elementId].x, y:this._spritesOffsets[elementId].y};
     }
     else if (this.state === Reel.State.Rotating )
     {
     return {x:this._blurOffsets[elementId].x, y:this._blurOffsets[elementId].y};//cc.p(this._blurOffsets[elementId]);
     }
     else if (this.state === Reel.State.Stopping)
     {
     // тут в зависимости от индекса и уже показанных занчимых символов

     return {x:this._spritesOffsets[elementId].x, y:this._spritesOffsets[elementId].y};//cc.p(this._spritesOffsets[elementId]);
     }
     }

     return {x:this._spritesOffsets[elementId].x, y:this._spritesOffsets[elementId].y};//cc.p(this._spritesOffsets[elementId]);
     };*/



    /**
     * Updates reel symbols geometry. It's another magic method which makes reel rotate.
     * @memberOf Reel
     * @private
     * */
    Reel.prototype._updateGeometry = function() {
        //cc.log("UPDATE BEGIN");

        // координата верхнего левого угла барабана
        var cellPosition = {x:0, y:0};//{x:this.container.position.x, y:this.container.position.y};

        /// ТУТ НУЖНО УЧИТЫВАТЬ направление вращения (offset * rotation SIGN)

        if (this.state !== Reel.State.Bouncing) {
            this._directionalOffset = (this._rotationDirection === Reel.RotationDirection.Down) ? this.offset : -this.offset;
        }
        //  для состояния же отскока _directionalOffset уже имеет знаковое значение

        var HIDDEN_SYMBOL_PART = 0;
        var VISIBLE_IMAGE_PART = 0;
        var cropped = 0;

        for (var i = 0; i < (this._kROWS + 2); ++i) {

            var node = this._cells[i].getChildAt((this._isBlurEnabled && this.state === Reel.State.Rotating) ? 1 : 0);

            // оригинальное положение на текстурном атласе
            var rect = this._rectForSymbolAtIndex(i);

            var textureOffset = {x:0, y:0};//this._textureOffsetForSymbolAtIndex(i);

            cellPosition.y = Math.floor(cellPosition.y + 0.5);

            var correctedPosition = {x:cellPosition.x, y:cellPosition.y};

            // correctedPosition.y = Math.floor(correctedPosition.y + 0.5);

            // верхний заэкранный элемент
            if (i === 0) {
                // элемент виден только если смещение положительное (вниз)
                node.visible = this._directionalOffset > 0.0;

                if (this._rotationDirection === Reel.RotationDirection.Down) {

                    if (this._directionalOffset > 0.0) {
                        HIDDEN_SYMBOL_PART = Math.floor(this._kCELLSIZE.height - Math.abs(this._directionalOffset));
                        VISIBLE_IMAGE_PART = (Math.abs(textureOffset.y) + rect.height - HIDDEN_SYMBOL_PART);

                        // если символ торчит видимой частью изображения из за экрана
                        //            if(VISIBLE_IMAGE_PART > 0.0f)
                        {
                            // если отрезаная сверху пустая часть спрайта больше чем скрытая за экраном, то нужно только скорректировать координату вывода картинки
                            if (Math.abs(textureOffset.y) > HIDDEN_SYMBOL_PART)
                            {
                                // !!!!!!!!!!!!!!!!!!!!!!!!
                                correctedPosition.y += HIDDEN_SYMBOL_PART - Math.abs(textureOffset.y);
                                correctedPosition.y = Math.floor(correctedPosition.y + 0.5);
                            }
                            // иначе картинка частично за экраном
                            else
                            {
                                rect.y += rect.height - VISIBLE_IMAGE_PART;
                                rect.y = Math.floor(rect.y + 0.5);
                                rect.height = VISIBLE_IMAGE_PART;
                                rect.height = Math.floor(rect.height + 0.5);
                            }

                            node.texture.frame = rect;
                            //node.texture.frame = this._rectForSymbolAtIndex(i+1);
                            // node.texture._updateUvs();
                            node.position = correctedPosition;
                        }

                        // переходим к координате следующей ячейки
                        cellPosition.y += VISIBLE_IMAGE_PART;
                    }
                }
                else if (this._rotationDirection === Reel.RotationDirection.Up) {
                    // при вращении вверх никаких манипуляций с этим элементов не делаем т.к. он обычно вне области барабана
                }
            }
            // элемент верхнего ряда барабана
            else if (i === 1) {
                if( this._rotationDirection === Reel.RotationDirection.Up) {
                    // переходим к координате следующей ячейки
                    cellPosition.y += this._kCELLSIZE.height + this.offset;
                }
                else if (this._rotationDirection === Reel.RotationDirection.Down) {

                    if (this._directionalOffset >= 0) {
                        node.texture.frame = rect;
                        // node.texture._updateUvs();
                        // correctedPosition.y = Math.floor(correctedPosition.y + 0.5); // !!!!!!!!!!!!!!
                        node.position = correctedPosition;

                        cellPosition.y += this._kCELLSIZE.height;
                    }
                    // _directionalOffset < 0
                    else {
                        HIDDEN_SYMBOL_PART = Math.floor(Math.abs(this._directionalOffset));
                        VISIBLE_IMAGE_PART = Math.abs(textureOffset.y) + rect.height - HIDDEN_SYMBOL_PART;

                        // если символ торчит видимой частью изображения из за экрана
                        //            if(VISIBLE_IMAGE_PART > 0.0f)
                        {
                            // если отрезаная сверху пустая часть спрайта больше чем скрытая за экраном, то нужно только скорректировать координату вывода картинки
                            if (Math.abs(textureOffset.y) > HIDDEN_SYMBOL_PART) {
                                // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                correctedPosition.y += HIDDEN_SYMBOL_PART - Math.abs(textureOffset.y);
                                correctedPosition.y = Math.floor(correctedPosition.y + 0.5);
                            }
                            // иначе картинка частично за экраном
                            else {
                                // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                rect.y += rect.height - VISIBLE_IMAGE_PART;
                                rect.y = Math.floor(rect.y + 0.5);
                                rect.height = VISIBLE_IMAGE_PART;
                                rect.height = Math.floor(rect.height + 0.5);
                            }

                            node.texture.frame = rect;
                            // node.texture._updateUvs();
                            node.position = correctedPosition;
                        }

                        // переходим к координате следующей ниже ячейки
                        // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                        cellPosition.y += this._kCELLSIZE.height - HIDDEN_SYMBOL_PART;
                    }
                }
            }
            // нижний элемент ряда на барабане
            else if (i === this._kROWS) {
                if( this._rotationDirection === Reel.RotationDirection.Down) {
                    if (this._directionalOffset > 0) {

                        // подрезаем выводимую часть до нижнего края барабана
                        if (Math.abs(textureOffset.y) + rect.height > this._kCELLSIZE.height - Math.abs(this._directionalOffset)) {
                            cropped = (Math.abs(textureOffset.y) + rect.height - (this._kCELLSIZE.height - Math.abs(this._directionalOffset)));
                            rect.height -= cropped;
                            rect.height = Math.floor(rect.height/* + 0.5*/);
                        }
                    }
                }

                node.texture.frame = rect;
                // node.texture._updateUvs();
                node.position = correctedPosition;

                cellPosition.y += this._kCELLSIZE.height;
            }
            // заэкранный элемент
            else if (i === (this._kROWS + 1)) {

                if (this._directionalOffset >= 0.0) {
                    node.visible = false;
                }
                else
                {
                    node.visible = true;

                    if ( this._rotationDirection === Reel.RotationDirection.Up) {
                        // ТУТ КОД ПО АНАЛОГИИ С КОДОМ ВЕРХНЕГО ЗАЭКРАННОГО ЭЛЕМЕНТА
                    }
                    else if ( this._rotationDirection === Reel.RotationDirection.Down) {
                        if (this._directionalOffset < 0) {
                            // подрезаем выводимую часть до нижнего края барабана
                            if (Math.abs(textureOffset.y) + rect.height > this._kCELLSIZE.height - (this._kCELLSIZE.height - Math.abs(this._directionalOffset))) {
                                cropped = (Math.abs(textureOffset.y) + rect.height - (this._kCELLSIZE.height - (this._kCELLSIZE.height - Math.abs(this._directionalOffset))));
                                rect.height -= cropped;
                                rect.height = Math.floor(rect.height + 0.5);
                            }

                            node.texture.frame = rect;
                            // node.texture._updateUvs();
                            node.position = correctedPosition;
                        }
                    }
                }
            }
            // какой-то промежуточный элемент который целиком всегда на экране
            else {
                node.texture.frame = rect;
                // node.texture._updateUvs();
                node.position = correctedPosition;

                cellPosition.y += this._kCELLSIZE.height;
            }
        }
    };

    /**
     * Sets rotation direction.
     * @param {Reel.RotationDirection} direction Rotation direction
     * @memberOf Reel
     * */
    Reel.prototype.setRotationDirection = function(direction) {
        this._rotationDirection = direction;
    };

    /**
     * Sets blur symbols.
     * @param {Number[]} symbols Array of permissive symbol codes for blur
     * @memberOf Reel
     * */
    Reel.prototype.setBlurElements = function(symbols) {
        if (this.state === Reel.State.Stopping) {
            //        cc.warn("YOU SHOULD NOT SET BLURRING ELEMENTS IN STOPPING MODE");
            return;
        }

        //   cc.assert(symbols.length > 0, "symbols is empty!");

        if (symbols.length == 0) return;


        this._blurElements = [];
        for (var i = 0; i < symbols.length; i++) {
            this._blurElements.push(symbols[i]);
        }

        // this._blurElements = symbols.slice(0); //copy array

//        this._correctOutsideElements();
    };

    /**
     * Resets blur permissive symbols codes to default (all allowed to be in blur).
     * @memberOf Reel
     * */
    Reel.prototype.resetBlurElements = function() {
        if (this.state === Reel.State.Stopping) {
            //        cc.warn("YOU SHOULD NOT SET BLURRING ELEMENTS IN STOPPING MODE");
            return;
        }

        this._blurElements = [];

        for (var i = 0; i < this._kELEMENTS; ++i) {
            this._blurElements.push(i);
        }

        this._correctOutsideElements();
    };

    //замена нижнего и верхнего скрытых символов, если они содержат не входящие в блюр элементы
    /**
     * Corrects out of reel elements (topmost and bottom). Only permissive symbols are allowed.
     * @private
     * @memberOf Reel
     * */
    Reel.prototype._correctOutsideElements = function() {

        var changeElements = false;
        for (var i = 0; i < this._kROWS + 2; i += this._kROWS + 1) {

            var found = false;
            for (var j = 0; j < this._blurElements.length; ++j) {
                if (this._blurElements[j] === this._elements[i]) {
                    found = true;
                    break;
                }
            }

            if (!found) {
                var symbolId = this._makeSymbol();

                this._elements[i] = symbolId;

                var node = this._cells[i].getChildAt(0);
                node.texture.frame = this._spritesOnAtlas[symbolId].clone();
                // node.texture._updateUvs();

                changeElements = true;
            }
        }

        if (changeElements) {
            this._updateGeometry();
        }
    };

    /**
     * Sets reel infinite rotation mode.
     * @param {Boolean} infiniteRotate true - reel rotates ignoring timeouts, false - reel rotates and stops after timeout
     * @memberOf Reel
     * */
    Reel.prototype.setInfiniteRotate = function(infiniteRotate) {
        if (this._infiniteRotate == true) {
            if (this.state === Reel.State.Stopping || this.state === Reel.State.Bouncing) {
                //            cc.warn("YOU SHOULD NOT SET ENABLED INFINITE ROTATE IN STOPPING OR BOUNCING MODE");
                return;
            }
        }

        this._infiniteRotate = infiniteRotate;
    };

    /**
     * Sets previous reel links
     * @param {Reel} reel Previous reel
     * @memberOf Reel
     * */
    Reel.prototype.setPreviousReel = function(reel) {
        this._prevReel = reel;
    };

    /**
     * Draws reel in defauls state.
     * @memberOf Reel
     * @private
     * */
    Reel.prototype._redrawToLastState = function() {
        this.offset = 0;
        this.state = Reel.State.Idle;
        this._updateGeometry();
    };

    /**
     * Sets symbol visible or not.
     * @param {Boolean} visible Symbol visible or not
     * @param {Number} index Symbol index on reel (from top to bottom)
     * @memberOf Reel
     * */
    Reel.prototype.setVisibleForSymbolWithIndex = function(visible, index) {

  //      console.log((visible ? "SHOWING " : "HIDING ")  + "SYMBOLS AT: " + index + " POSITION");

        if (this.state === Reel.State.Stopping) {
            //        cc.warn("YOU SHOULD NOT HIDE ELEMENTS IN STOPPING MODE");
            return;
        }

        //верхний символ - невидимый
        this._cells[index + 1].visible = visible;
    };

    /**
     * Makes all symbols visible.
     * @memberOf Reel
     * */
    Reel.prototype.showAllSymbols = function() {
        if (this.state === Reel.State.Stopping) {
            //        cc.warn("YOU SHOULD NOT HIDE ELEMENTS IN STOPPING MODE");
            return;
        }

        for (var i = 1; i < this._kROWS + 1; ++i) {
            this._cells[i].visible = true;
        }
    };

    Reel.prototype.freeze = function () {
        this._isFreezed = true;
    };

    Reel.prototype.unfreeze = function () {
        this._isFreezed = false;
    };

    Reel.prototype.isFreezed = function () {
        return this._isFreezed;
    };

    global.Reel = Reel;
}(typeof module === 'object' && module.exports || this));



//////////////////
// WEBPACK FOOTER
// ./src/library/reel.js
// module id = 122
// module chunks = 0